import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherEngagementView.css';

function TeacherEngagementView({ teacherData }) {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [students, setStudents] = useState([]);
  const [engagementData, setEngagementData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      let classesData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Also load classes where subjectTeacherAssignments contains the teacher's subject
      if (teacherData.specialtySubject) {
        const allClassesQuery = query(collection(db, 'classes'));
        const allSnapshot = await getDocs(allClassesQuery);
        const allClasses = allSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const subjectClasses = allClasses.filter(c =>
          (c.subjectTeacherAssignments || []).includes(teacherData.specialtySubject) &&
          !classesData.find(ec => ec.id === c.id)
        );
        classesData = [...classesData, ...subjectClasses];
      }

      setClasses(classesData);
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadEngagementData = useCallback(async (classId) => {
    if (!classId) {
      setStudents([]);
      setEngagementData({});
      return;
    }
    setLoadingData(true);
    try {
      // Load students in the class
      const studentsQuery = query(
        collection(db, 'classes', classId, 'students'),
        where('isActive', '==', true)
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      const studentsData = studentsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setStudents(studentsData);

      // iOS stores studentId as the student's UUID (doc id / uuid field), not the
      // school studentId. Map every known identifier -> canonical doc id so
      // iOS-logged activity resolves to the right student row (case-insensitive).
      const studentKeyMap = {};
      studentsData.forEach(s => {
        [s.id, s.uuid, s.studentId, s.userId].filter(Boolean).forEach(k => {
          studentKeyMap[String(k).toLowerCase()] = s.id;
        });
      });
      const resolveStudentId = (raw) =>
        raw ? (studentKeyMap[String(raw).toLowerCase()] || null) : null;

      // Initialize engagement data for each student
      const engagement = {};
      const ensureEntry = (studentId) => {
        if (!engagement[studentId]) {
          engagement[studentId] = {
            viewed: 0,
            started: 0,
            submitted: 0,
            materialsOpened: 0,
            onlineClassesJoined: 0
          };
        }
        return engagement[studentId];
      };

      // Load assignment submissions for this class.
      // iOS writes to `assignmentSubmissions` (NOT `submissions`).
      const assignmentsQuery = query(collection(db, 'classes', classId, 'assignments'));
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const assignments = assignmentsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      for (const assignment of assignments) {
        const submissionsSnapshot = await getDocs(
          collection(db, 'classes', classId, 'assignments', assignment.id, 'assignmentSubmissions')
        );
        submissionsSnapshot.docs.forEach(subDoc => {
          const sub = subDoc.data();
          const studentId = resolveStudentId(sub.studentId);
          if (!studentId) return;
          const entry = ensureEntry(studentId);
          // iOS SubmissionStatus: Assigned/Started/Submitted/Late/Graded/Returned
          const status = (sub.status || '').toLowerCase();
          if (['submitted', 'late', 'graded', 'returned'].includes(status) || sub.submittedAt) {
            entry.submitted += 1;
          }
          if (['started', 'submitted', 'late', 'graded', 'returned'].includes(status) || sub.startedAt) {
            entry.started += 1;
          }
          if (sub.viewedAt || status === 'viewed') {
            entry.viewed += 1;
          }
        });
      }

      // Load learning activity logs.
      // iOS logs to `learningActivities` with activityType strings like
      // "Assignment Viewed" / "Online Class Joined" (LearningActivityType enum).
      // `studentActivityLogs` is the legacy scanner collection — kept for old docs.
      const activityTypes = (log) => [log.activityType, log.type].filter(Boolean).map(t => String(t).toLowerCase());
      const countActivity = (studentId, types) => {
        const entry = ensureEntry(studentId);
        if (types.includes('assignment viewed') || types.includes('assignment_viewed')) entry.viewed += 1;
        if (types.includes('assignment started') || types.includes('assignment_started')) entry.started += 1;
        if (types.includes('assignment submitted') || types.includes('assignment_submitted') ||
            types.includes('assignment graded') || types.includes('assignment_graded')) entry.submitted += 1;
        if (types.includes('learning material opened') || types.includes('material_opened')) entry.materialsOpened += 1;
        if (types.includes('online class joined') || types.includes('online_class_joined') ||
            types.includes('online class participated') || types.includes('online_class_participated')) entry.onlineClassesJoined += 1;
      };

      // classId is stored as a UUID string — iOS writes uppercase uuidString,
      // web doc ids may be lowercase, so query all casing variants.
      const classIdVariants = [...new Set([classId, String(classId).toLowerCase(), String(classId).toUpperCase()])];
      const activityDocs = new Map();
      for (const cid of classIdVariants) {
        const activitySnapshot = await getDocs(
          query(collection(db, 'learningActivities'), where('classId', '==', cid))
        );
        activitySnapshot.docs.forEach(d => activityDocs.set(d.id, d.data()));
      }
      activityDocs.forEach(log => {
        const studentId = resolveStudentId(log.studentId);
        if (!studentId) return;
        countActivity(studentId, activityTypes(log));
      });

      // Legacy scanner collection — tolerate old web data too.
      const legacyActivitySnapshot = await getDocs(
        query(collection(db, 'studentActivityLogs'), where('classId', '==', classId))
      );
      legacyActivitySnapshot.docs.forEach(logDoc => {
        const log = logDoc.data();
        const studentId = resolveStudentId(log.studentId) || log.studentId;
        if (!studentId) return;
        countActivity(studentId, activityTypes(log));
      });

      // Ensure all students have an entry
      studentsData.forEach(student => {
        if (!engagement[student.id]) {
          engagement[student.id] = {
            viewed: 0,
            started: 0,
            submitted: 0,
            materialsOpened: 0,
            onlineClassesJoined: 0
          };
        }
      });

      setEngagementData(engagement);
    } catch (error) {
      console.error('Error loading engagement data:', error);
      setStudents([]);
      setEngagementData({});
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      loadEngagementData(selectedClassId);
    } else {
      setStudents([]);
      setEngagementData({});
    }
  }, [selectedClassId, loadEngagementData]);

  const calculateAverages = () => {
    if (students.length === 0) return null;
    const totals = { viewed: 0, started: 0, submitted: 0, materialsOpened: 0, onlineClassesJoined: 0 };
    students.forEach(student => {
      const data = engagementData[student.id] || totals;
      totals.viewed += data.viewed || 0;
      totals.started += data.started || 0;
      totals.submitted += data.submitted || 0;
      totals.materialsOpened += data.materialsOpened || 0;
      totals.onlineClassesJoined += data.onlineClassesJoined || 0;
    });
    return {
      viewed: (totals.viewed / students.length).toFixed(1),
      started: (totals.started / students.length).toFixed(1),
      submitted: (totals.submitted / students.length).toFixed(1),
      materialsOpened: (totals.materialsOpened / students.length).toFixed(1),
      onlineClassesJoined: (totals.onlineClassesJoined / students.length).toFixed(1)
    };
  };

  const averages = calculateAverages();

  if (loading) {
    return (
      <div className="teacher-engagement-view">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="teacher-engagement-view">
      <h2>Student Engagement</h2>

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Classes Assigned</h3>
          <p>You need a class assigned to view student engagement.</p>
        </div>
      ) : (
        <>
          <div className="engagement-controls">
            <div className="class-selector">
              <label>Select Class:</label>
              <select
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} {cls.grade ? `- ${cls.grade}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingData ? (
            <div className="loading">Loading engagement data...</div>
          ) : students.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>No Students</h3>
              <p>No active students found in this class.</p>
            </div>
          ) : (
            <>
              {averages && (
                <div className="class-averages">
                  <h3>Class Average Engagement</h3>
                  <div className="averages-grid">
                    <div className="average-card">
                      <span className="avg-icon">👁️</span>
                      <span className="avg-value">{averages.viewed}</span>
                      <span className="avg-label">Viewed</span>
                    </div>
                    <div className="average-card">
                      <span className="avg-icon">✏️</span>
                      <span className="avg-value">{averages.started}</span>
                      <span className="avg-label">Started</span>
                    </div>
                    <div className="average-card">
                      <span className="avg-icon">✅</span>
                      <span className="avg-value">{averages.submitted}</span>
                      <span className="avg-label">Submitted</span>
                    </div>
                    <div className="average-card">
                      <span className="avg-icon">📚</span>
                      <span className="avg-value">{averages.materialsOpened}</span>
                      <span className="avg-label">Materials Opened</span>
                    </div>
                    <div className="average-card">
                      <span className="avg-icon">📹</span>
                      <span className="avg-value">{averages.onlineClassesJoined}</span>
                      <span className="avg-label">Classes Joined</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="engagement-table-container">
                <table className="engagement-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Viewed</th>
                      <th>Started</th>
                      <th>Submitted</th>
                      <th>Materials Opened</th>
                      <th>Classes Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => {
                      const data = engagementData[student.id] || { viewed: 0, started: 0, submitted: 0, materialsOpened: 0, onlineClassesJoined: 0 };
                      return (
                        <tr key={student.id}>
                          <td className="student-cell">
                            <div className="student-info-cell">
                              {student.photoURL ? (
                                <img src={student.photoURL} alt={student.fullName} className="student-avatar" />
                              ) : (
                                <div className="student-avatar placeholder">👤</div>
                              )}
                              <span>{student.fullName}</span>
                            </div>
                          </td>
                          <td>{data.viewed || 0}</td>
                          <td>{data.started || 0}</td>
                          <td>{data.submitted || 0}</td>
                          <td>{data.materialsOpened || 0}</td>
                          <td>{data.onlineClassesJoined || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TeacherEngagementView;
